'use client';

/**
 * WebPricingSectionCard — three-tier pricing comparison.
 *
 * Visual structure ported from
 * `klyde-website/src/components/landing/PricingSection.tsx`. Content
 * lives in `klyde_web_content/data/web_landing.yaml` (slug=
 * web_landing_pricing, kind=pricing_section) and is served via the
 * `web_landing_pricing` BFF strategy resolved through
 * (category=content, action=web_landing_pricing).
 *
 * Typography slots from WEB_SECTION bundle:
 *   header        = eyebrow ("PRICING")
 *   name          = section headline
 *   section_label = tier name (Lite / Pro / Edge)
 *   amount        = tier price
 *   subtitle      = price_note (small line under price)
 *   body          = feature list items
 *
 * Per-tier chrome (border + background by `highlight`, badge pill,
 * CTA primary/secondary) lives in the renderer — that's structural
 * decoration, not typography.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, XCircle, Check, DollarSign } from 'lucide-react';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { api } from '@/lib/api/client';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import { useLanguageStore } from '@/lib/stores/language-store';
import type {
  WebPricingSectionContent,
  WebPricingTier,
} from '@/lib/api/types';


export function WebPricingSectionCard() {
  const T = useCardTypography();
  const strategy = useCapabilityStore(
    (s) => s.strategyName('content', 'web_landing_pricing'),
  );
  const lang = useLanguageStore((s) => s.lang) ?? undefined;

  const [data, setData] = useState<WebPricingSectionContent | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Animation pattern note — earlier version used framer-motion's
  // `useInView` to trigger entry animations on scroll. That broke
  // inside Canvas2Card's nested overflow-y-auto: the default
  // IntersectionObserver root is the document viewport, not our
  // scroll container, so `isInView` stayed false and cards rendered
  // at opacity:0 forever. Switched to mount-time animation (same
  // pattern Hero uses). Re-enable scroll-triggered choreography
  // when Canvas2Card exposes its scroll element as a viewport.root.

  useEffect(() => {
    if (!strategy) return;
    let cancelled = false;
    api
      .getStrategyData<WebPricingSectionContent>(strategy, lang ? { lang } : {})
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setError(res.error || 'Failed to load pricing');
          return;
        }
        setData(res.data ?? null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message ?? 'Network error');
      });
    return () => { cancelled = true; };
  }, [strategy, lang]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-destructive">
        <XCircle className="h-6 w-6 mb-2" />
        <p className={T.body}>{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Loading pricing…
      </div>
    );
  }

  return (
    <section
      id="pricing"
      className="web-surface relative py-24 px-6 bg-background text-foreground overflow-hidden"
    >
      {/* Subtle radial atmosphere — same family as the hero, smaller dose */}
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          background:
            'radial-gradient(circle at 50% 0%, hsl(var(--primary) / 0.10) 0%, transparent 60%)',
        }}
      />

      <div className="container mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className={cn(T.header, 'mb-4')}>{data.eyebrow}</p>
          <h2 className={T.name}>{data.headline}</h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {data.tiers.map((tier, i) => (
            <TierCard
              key={tier.name}
              tier={tier}
              index={i}
              T={T}
            />
          ))}
        </div>
      </div>
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────────
// TierCard — one tier column
// ─────────────────────────────────────────────────────────────────────

interface TierCardProps {
  tier: WebPricingTier;
  index: number;
  T: ReturnType<typeof useCardTypography>;
}

function TierCard({ tier, index, T }: TierCardProps) {
  return (
    <motion.div
      className={cn(
        'relative rounded-xl border p-8 flex flex-col gap-6',
        tier.highlight
          ? 'border-emerald-500/60 bg-emerald-950/20 dark:bg-emerald-950/20'
          : 'border-border bg-card/50',
      )}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 + index * 0.1, duration: 0.5 }}
    >
      {tier.badge && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
          {tier.badge}
        </span>
      )}

      <div>
        <h3 className={cn(T.sectionLabel, 'mb-1')}>{tier.name}</h3>
        {tier.descriptor && (
          <p className={cn(T.subtitle, 'mb-2')}>{tier.descriptor}</p>
        )}
        <p className={T.amount}>{tier.price}</p>
        <p className={cn(T.subtitle, 'mt-1')}>{tier.price_note}</p>
      </div>

      <ul className="flex flex-col gap-2 flex-1">
        {tier.features.map((f) => (
          <li key={f} className={cn(T.body, 'flex items-start gap-2')}>
            <Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <a
        href={tier.cta.href}
        className={cn(
          'w-full rounded-full py-2.5 text-sm font-medium text-center transition-colors',
          tier.cta_primary
            ? 'bg-primary text-primary-foreground hover:opacity-90'
            : 'border border-border text-foreground hover:bg-muted/20',
        )}
      >
        <span className="inline-flex items-center justify-center gap-1.5">
          <DollarSign className="h-4 w-4" />
          {tier.cta.label}
        </span>
      </a>
    </motion.div>
  );
}
